/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import React, { useCallback, useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { useAppDispatch, useAppSelector } from '../../../../store';
import { removeAllErrors, removeMessage } from '../../store/messages/actions';
import Notification from './Notification';

import './NotificationsContainer.scss';

const NotificationsContainer = () => {
  const dispatch = useAppDispatch();

  const [isExpanded, setIsExpanded] = useState(false);

  const { errors, nonErrors, total } = useAppSelector(state => {
    const messages = state?.messages?.messages ?? [];
    return {
      errors: messages.filter(m => m.status === 'error'),
      nonErrors: messages.filter(m => m.status !== 'error'),
      total: messages.length
    };
  }, shallowEqual);

  useEffect(() => {
    if (total === 0) {
      setIsExpanded(false);
    }
  }, [total]);

  const handleExpandErrors = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const handleOnClose = useCallback(
    key => {
      dispatch(removeMessage(key));
    },
    [dispatch]
  );

  const handleRemoveErrors = useCallback(() => {
    dispatch(removeAllErrors());
  }, [dispatch]);

  if (total === 0) {
    return null;
  }

  const renderErrors = () => {
    if (errors.length === 0) {
      return null;
    }

    if (!isExpanded) {
      return (
        <Notification
          {...errors[0]}
          id={errors[0].key}
          key={errors[0].key}
          errorsCount={errors.length}
          onExpand={handleExpandErrors}
          onClose={handleOnClose}
        />
      );
    }

    return errors.map((n, i) => (
      <Notification
        {...n}
        id={n.key}
        key={n.key}
        errorsCount={i === 0 ? errors.length : 0}
        removeAllErrors={i === 0 ? handleRemoveErrors : undefined}
        onClose={handleOnClose}
      />
    ));
  };

  return (
    <div className="NotificationsContainer">
      {renderErrors()}
      {nonErrors.map(n => (
        <Notification {...n} id={n.key} key={n.key} onClose={handleOnClose} />
      ))}
    </div>
  );
};

export default NotificationsContainer;
